/**
 * Bundled truck-type catalog — the OFFLINE / FIRST-RUN FALLBACK only (#249). Every picker loads
 * GET /api/v1/truck-types through `hooks/useTruckCatalog.ts`; this copy of today's 10 types is
 * used while that first load is in flight or when it fails, and for name lookups on an older
 * API that does not send `vehicleTypeName`. Labels use SXL/MXL abbreviations by design and match
 * the shipper app's bundled fallback (rhino_core truck_catalog.dart).
 */

export interface TruckTypeOption {
  id: string;
  name: string;
  capacityTons: string;
  lengthFt: string;
  idealFor: string;
}

export interface TruckCategoryOption {
  id: string;
  name: string;
  description: string;
  trucks: TruckTypeOption[];
}

/** Default pre-selected truck type on shipment creation. */
export const DEFAULT_TRUCK_TYPE_ID = 'container_32_mxl';

export const TRUCK_CATALOG: TruckCategoryOption[] = [
  {
    id: 'closed_container',
    name: 'Closed Container',
    description: 'Best for high-value, weather-sensitive cargo.',
    trucks: [
      {
        id: 'container_20_sxl',
        name: '20 ft Container (SXL)',
        capacityTons: '6 - 7.5',
        lengthFt: '20',
        idealFor: 'Electronics, pharmaceuticals, garments',
      },
      {
        id: 'container_32_sxl',
        name: '32 ft Container (SXL)',
        capacityTons: '7 - 9',
        lengthFt: '32',
        idealFor: 'E-commerce, FMCG, voluminous light goods',
      },
      {
        id: 'container_32_mxl',
        name: '32 ft Container (MXL)',
        capacityTons: '15 - 18',
        lengthFt: '32',
        idealFor: 'Heavy machinery, dense auto parts, white goods',
      },
    ],
  },
  {
    id: 'open_flatbed',
    name: 'Open Body & Flatbed',
    description: 'Best for industrial raw materials and crane/forklift loading.',
    trucks: [
      {
        id: 'open_6_tyre',
        name: '6-Tyre Open Truck',
        capacityTons: '7 - 9',
        lengthFt: '17 - 19',
        idealFor: 'Agricultural goods, plywood, light hardware',
      },
      {
        id: 'open_taurus',
        name: 'Taurus (10/12 Tyre Open)',
        capacityTons: '10 - 16',
        lengthFt: '22 - 28',
        idealFor: 'Cement bags, steel rods, bulk raw materials',
      },
      {
        id: 'flatbed_trailer',
        name: 'Flatbed Trailer',
        capacityTons: '16 - 25+',
        lengthFt: '40',
        idealFor: 'Steel coils, heavy pipes, structural components',
      },
    ],
  },
  {
    id: 'heavy_articulated',
    name: 'Heavy & ODC Trailers',
    description: 'For massive long-haul or over-dimensional industrial loads.',
    trucks: [
      {
        id: 'semi_trailer_40',
        name: '40 ft Semi-Trailer',
        capacityTons: '16 - 25+',
        lengthFt: '40',
        idealFor: 'Long-haul bulk freight, heavy industrial goods',
      },
      {
        id: 'low_bed_odc',
        name: 'Low-Bed / ODC Trailer',
        capacityTons: 'Custom / 25+',
        lengthFt: 'Variable',
        idealFor: 'Boilers, excavators, wind turbine parts (Requires Permits)',
      },
    ],
  },
  {
    id: 'special_purpose',
    name: 'Special Purpose',
    description: 'Temperature-controlled or specialized handling.',
    trucks: [
      {
        id: 'reefer',
        name: 'Reefer Truck',
        capacityTons: 'Variable',
        lengthFt: 'Variable',
        idealFor: 'Frozen food, dairy, fresh produce, pharma',
      },
      {
        id: 'tipper',
        name: 'Tipper / Dump Truck',
        capacityTons: 'Variable',
        lengthFt: 'Variable',
        idealFor: 'Sand, gravel, bricks, construction debris',
      },
    ],
  },
];

/** Flat list of all truck options across categories. */
export const ALL_TRUCK_TYPES: TruckTypeOption[] = TRUCK_CATALOG.flatMap((c) => c.trucks);

/** Look up a truck (and its category) by catalog id; undefined for unknown ids. */
export function findTruckType(
  id: string,
): { category: TruckCategoryOption; truck: TruckTypeOption } | undefined {
  for (const category of TRUCK_CATALOG) {
    const truck = category.trucks.find((t) => t.id === id);
    if (truck) return { category, truck };
  }
  return undefined;
}
